import {
  ApiError,
  getHotelConfig,
  getLocationNicheConfig,
  upsertHotelConfig,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { HotelConfigResponse, InOutPolicy, UpsertHotelConfigRequest } from '@jq4race/shared';
import { Button, Card, CardContent, Input, Label } from '@jq4race/ui';
import { useNavigate, useParams } from '@tanstack/react-router';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Form values flowing through react-hook-form.
 * perExitFeeCents is stored in DOLLARS for display; converted to cents at submit.
 */
interface HotelConfigFormValues {
  overnightCutoffHour: 0 | 6;
  inOutPolicy: InOutPolicy;
  maxExits: number | null;
  /** Display value in dollars; converted to integer cents on submit. */
  perExitFeeDollars: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function dollarsToCents(v: string | number | null | undefined): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

function centsToDollars(cents: number | null | undefined): number | null {
  if (cents === null || cents === undefined) return null;
  return cents / 100;
}

function responseToFormValues(config: HotelConfigResponse): HotelConfigFormValues {
  return {
    overnightCutoffHour: config.overnightCutoffHour as 0 | 6,
    inOutPolicy: config.inOutPolicy,
    maxExits: config.maxExits ?? null,
    perExitFeeDollars: centsToDollars(config.perExitFeeCents),
  };
}

function formValuesToRequest(values: HotelConfigFormValues): UpsertHotelConfigRequest {
  const base = {
    overnightCutoffHour: values.overnightCutoffHour,
    inOutPolicy: values.inOutPolicy,
  };

  if (values.inOutPolicy === 'max_exits') {
    return { ...base, maxExits: values.maxExits, perExitFeeCents: null };
  }

  if (values.inOutPolicy === 'per_exit_fee') {
    return {
      ...base,
      maxExits: null,
      perExitFeeCents: dollarsToCents(values.perExitFeeDollars),
    };
  }

  // unlimited
  return { ...base, maxExits: null, perExitFeeCents: null };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HotelConfigPage() {
  const { t } = useTranslation('location-hotel-config');
  const { locationId } = useParams({ strict: false });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<HotelConfigFormValues>({
    defaultValues: {
      overnightCutoffHour: 6,
      inOutPolicy: 'unlimited',
      maxExits: null,
      perExitFeeDollars: null,
    },
  });

  const inOutPolicy = watch('inOutPolicy');

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      // REQ-7.2 — gate page to hotel-niche locations. Check niche before loading config.
      // If the location has an activated niche that is NOT hotel, redirect immediately
      // rather than waiting for the backend 422 niche_mismatch error.
      const nicheConfig = await getLocationNicheConfig(locationId);
      if (nicheConfig.activated && nicheConfig.niche !== 'hotel') {
        void navigate({
          to: '/dashboard/locations/$locationId/niche-config',
          params: { locationId },
        });
        return;
      }

      const config = await getHotelConfig(locationId);
      reset(responseToFormValues(config));
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) {
        // Non-hotel location: niche_mismatch — redirect to niche config so admin
        // can see the current niche and correct it (REQ-7.2).
        void navigate({
          to: '/dashboard/locations/$locationId/niche-config',
          params: { locationId },
        });
        return;
      }
      setErrorBanner(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, navigate, reset, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-clear policy-specific fields when policy changes
  useEffect(() => {
    if (inOutPolicy !== 'max_exits') setValue('maxExits', null);
    if (inOutPolicy !== 'per_exit_fee') setValue('perExitFeeDollars', null);
  }, [inOutPolicy, setValue]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  async function handleFormSubmit(values: HotelConfigFormValues) {
    if (!locationId) return;
    setErrorBanner(null);
    try {
      const request = formValuesToRequest(values);
      const saved = await upsertHotelConfig(locationId, request);
      reset(responseToFormValues(saved));
      setSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.saveError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.unexpectedError') }));
      }
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
              {/* Overnight cutoff hour */}
              <Field
                label={t('form.overnightCutoffHour.label')}
                htmlFor="overnightCutoffHour"
                error={errors.overnightCutoffHour?.message}
              >
                <select
                  id="overnightCutoffHour"
                  disabled={isSubmitting}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  {...register('overnightCutoffHour', { valueAsNumber: true })}
                >
                  <option value={0}>{t('form.overnightCutoffHour.optionMidnight')}</option>
                  <option value={6}>{t('form.overnightCutoffHour.option6am')}</option>
                </select>
                <p className="mt-1 text-[12px] text-jq-graphite">
                  {t('form.overnightCutoffHour.hint')}
                </p>
              </Field>

              {/* In/out policy */}
              <Field
                label={t('form.inOutPolicy.label')}
                htmlFor="inOutPolicy"
                error={errors.inOutPolicy?.message}
              >
                <select
                  id="inOutPolicy"
                  disabled={isSubmitting}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  {...register('inOutPolicy')}
                >
                  <option value="unlimited">{t('form.inOutPolicy.optionUnlimited')}</option>
                  <option value="max_exits">{t('form.inOutPolicy.optionMaxExits')}</option>
                  <option value="per_exit_fee">{t('form.inOutPolicy.optionPerExitFee')}</option>
                </select>
              </Field>

              {/* max_exits: number input */}
              {inOutPolicy === 'max_exits' && (
                <Field
                  label={t('form.maxExits.label')}
                  htmlFor="maxExits"
                  error={errors.maxExits?.message}
                >
                  <Input
                    id="maxExits"
                    type="number"
                    min={1}
                    step={1}
                    placeholder={t('form.maxExits.placeholder')}
                    disabled={isSubmitting}
                    {...register('maxExits', { valueAsNumber: true })}
                  />
                  <p className="mt-1 text-[12px] text-jq-graphite">{t('form.maxExits.hint')}</p>
                </Field>
              )}

              {/* per_exit_fee: dollar amount */}
              {inOutPolicy === 'per_exit_fee' && (
                <Field
                  label={t('form.perExitFee.label')}
                  htmlFor="perExitFeeDollars"
                  error={errors.perExitFeeDollars?.message}
                >
                  <Input
                    id="perExitFeeDollars"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder={t('form.perExitFee.placeholder')}
                    disabled={isSubmitting}
                    {...register('perExitFeeDollars', { setValueAs: dollarsToCents })}
                  />
                  <p className="mt-1 text-[12px] text-jq-graphite">{t('form.perExitFee.hint')}</p>
                </Field>
              )}

              <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
                <Button type="submit" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? t('form.saving') : t('form.save')}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-hotel-config');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
